import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { BookingCalendarComponent } from '../booking-calendar/booking-calendar.component';
import { BookingCardComponent } from '../booking-card/booking-card.component';
import { BookingService } from '../../../../services/admin/booking.service';
import { Pagination } from '../../../../model/pagination';
import { AdminBooking } from '../../../../model/admin.models';

interface DayGroup {
  date: string;
  label: string;
  bookings: AdminBooking[];
}

@Component({
  selector: 'app-booking-dashboard',
  standalone: true,
  imports: [NgForOf, NgIf, BookingCalendarComponent, BookingCardComponent],
  templateUrl: './booking-dashboard.component.html',
  styleUrls: ['./booking-dashboard.component.scss']
})
export class BookingDashboardComponent implements OnInit, OnDestroy {
  currentMonth: Date = new Date();
  selectedDateStr: string | null = null;
  countsMap: Record<string, number> = {};
  dayGroups: DayGroup[] = [];
  totalBookings = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private bookingService: BookingService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadMonthData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMonthChanged(newDate: Date) {
    this.currentMonth = newDate;
    this.selectedDateStr = null;
    this.loadMonthData();
  }

  loadMonthData() {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const from = new Date(year, month, 1);
    const to = new Date(year, month + 1, 0);

    const dateFrom = this.formatDate(from);
    const dateTo = this.formatDate(to);

    this.bookingService.getDailyCounts(dateFrom, dateTo)
      .pipe(takeUntil(this.destroy$))
      .subscribe(counts => {
        this.countsMap = counts.reduce((acc, curr) => {
          acc[curr.date] = curr.count;
          return acc;
        }, {} as Record<string, number>);
      });

    const params = {
      page: 0,
      size: 200,
      dateFrom,
      dateTo
    };

    this.bookingService.getList(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const paginationData = res as unknown as Pagination<AdminBooking>;

          this.totalBookings = paginationData.totalElements;
          this.dayGroups = this.groupBookingsByDay(paginationData.content || []);
        },
        error: (err) => {
          console.error('Error loading bookings', err);
        }
      });
  }

  onDateSelected(dateStr: string) {
    this.selectedDateStr = dateStr;
    const el = document.getElementById('day-' + dateStr);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  openBooking(id: number) {
    this.router.navigate(['/admin', 'bookings', id]);
  }

  private groupBookingsByDay(bookings: AdminBooking[]): DayGroup[] {
    const groups: Record<string, AdminBooking[]> = {};

    bookings.forEach(b => {
      const date = b.flight.date;
      if (!groups[date]) groups[date] = [];
      groups[date].push(b);
    });

    return Object.keys(groups).sort().map(date => {
      const d = new Date(date);
      const options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'long' };
      let label = d.toLocaleDateString('ru-RU', options);
      label = label.charAt(0).toUpperCase() + label.slice(1);

      return {
        date,
        label,
        bookings: groups[date]
      };
    });
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
